import { useState } from 'react';
import { Check, FlaskConical, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ProjectReader, Reader } from '../lib/types';
import {
  READER_TEMPLATES,
  cleanDraft,
  setProjectReaders,
  toProjectReader,
} from '../services/readers';
import { Badge, Button, Modal } from './ui';
import { ReaderEditor } from './ReaderManager';
import NewReaderSetup from './NewReaderSetup';
import type { LearnedReader } from '../services/readings';

/**
 * Choosing what a project records.
 *
 * A new project starts with no readers and nothing to capture into. Its owner
 * picks from the readers that come with the app, their own, or builds one — and
 * only then does the capture bar appear. The choice is the project's, so
 * everyone who can edit it captures against the same fields.
 */

function FieldChips({ reader }: { reader: ProjectReader }) {
  return (
    <p className="mt-1.5 flex flex-wrap gap-1.5">
      {reader.fields.map((f) => (
        <span
          key={f.key}
          className="rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[11px] text-ink-300"
        >
          {f.label}
          {f.unit && <span className="text-ink-500"> {f.unit}</span>}
        </span>
      ))}
    </p>
  );
}

/** Readers that could be added: the app's own, then the person's, minus any already in. */
function useChoices(chosen: ProjectReader[], personal: Reader[]) {
  const inProject = new Set(chosen.map((r) => r.id));
  const templateIds = new Set(READER_TEMPLATES.map((t) => t.id));
  return {
    templates: READER_TEMPLATES.filter((t) => !inProject.has(t.id)),
    // Older accounts hold personal copies of the app's readers under the same
    // ids; the template stands in for those so the list does not show both.
    mine: personal
      .filter((r) => !templateIds.has(r.id) && !inProject.has(r.id))
      .map(toProjectReader),
  };
}

function ChoiceList({
  title,
  readers,
  onAdd,
  busyId,
}: {
  title: string;
  readers: ProjectReader[];
  onAdd: (r: ProjectReader) => void;
  busyId: string | null;
}) {
  if (!readers.length) return null;
  return (
    <section className="mb-4">
      <h3 className="field-label mb-2">{title}</h3>
      <ul className="space-y-2">
        {readers.map((r) => (
          <li key={r.id} className="rounded-lg border border-ink-700 bg-ink-800/40 p-3">
            <div className="flex items-start gap-2">
              <FlaskConical size={15} className="mt-0.5 shrink-0 text-ink-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{r.name}</p>
                {r.description && <p className="mt-0.5 text-xs text-ink-400">{r.description}</p>}
                <FieldChips reader={r} />
              </div>
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus size={14} />}
                loading={busyId === r.id}
                disabled={busyId !== null}
                onClick={() => onAdd(r)}
              >
                Add
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The choices, used both on the empty project page and inside the Readers
 * dialog. Adding a reader writes it onto the project straight away.
 */
export function ReaderPicker({
  projectId,
  chosen,
  personal,
  onReview,
}: {
  projectId: string;
  chosen: ProjectReader[];
  personal: Reader[];
  /** Open a new reader's trial-photo readings for review. */
  onReview: (captureId: string, learned: LearnedReader) => void;
}) {
  const { templates, mine } = useChoices(chosen, personal);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(r: ProjectReader) {
    setBusyId(r.id);
    setError(null);
    try {
      await setProjectReaders(projectId, [...chosen, r]);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not add that reader.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <ChoiceList title="Come with the app" readers={templates} onAdd={(r) => void add(r)} busyId={busyId} />
      <ChoiceList title="Your readers" readers={mine} onAdd={(r) => void add(r)} busyId={busyId} />

      <Button variant="primary" icon={<Plus size={16} />} onClick={() => setCreating(true)}>
        Create a reader
      </Button>
      <p className="mt-2 text-xs text-ink-400">
        Give it a name and take a trial photo of a real result — the photo sets up the rest. It is
        saved to your account too, so other projects can use it.
      </p>

      {error && (
        <p className="mt-3 rounded-lg border border-red-800 bg-red-950 p-2.5 text-xs text-red-200">
          {error}
        </p>
      )}

      <NewReaderSetup
        open={creating}
        onClose={() => setCreating(false)}
        projectId={projectId}
        chosen={chosen}
        onReview={onReview}
      />
    </div>
  );
}

/** The Readers button on a reader-driven project. */
export function ProjectReadersDialog({
  open,
  onClose,
  projectId,
  isOwner,
  chosen,
  personal,
  onReview,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  isOwner: boolean;
  chosen: ProjectReader[];
  personal: Reader[];
  onReview: (captureId: string, learned: LearnedReader) => void;
}) {
  const [editing, setEditing] = useState<ProjectReader | null>(null);
  const [removing, setRemoving] = useState<ProjectReader | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <>
      <Modal open={open} onClose={onClose} title="What this project records" wide>
        <p className="mb-4 text-sm text-ink-400">
          {isOwner
            ? 'Everyone who can edit this project captures against these readers.'
            : 'Chosen by the project owner. You capture against these readers here.'}
        </p>

        {chosen.length === 0 ? (
          <p className="mb-4 rounded-xl border border-ink-700 bg-ink-900 p-6 text-center text-sm text-ink-400">
            Nothing chosen yet.
          </p>
        ) : (
          <ul className="mb-4 space-y-2">
            {chosen.map((r) => (
              <li key={r.id} className="rounded-lg border border-ink-700 bg-ink-800/40 p-3">
                <div className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-emerald-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{r.name}</span>
                      {r.builtIn && <Badge tone="neutral">comes with the app</Badge>}
                    </div>
                    {r.description && (
                      <p className="mt-0.5 text-xs text-ink-400">{r.description}</p>
                    )}
                    <FieldChips reader={r} />
                  </div>
                  {isOwner && (
                    <div className="flex shrink-0 gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        className="icon-btn text-ink-400 hover:bg-ink-800"
                        aria-label={`Edit ${r.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setRemoving(r)}
                        className="icon-btn text-ink-400 hover:bg-red-950 hover:text-red-300"
                        aria-label={`Remove ${r.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {isOwner &&
          (adding ? (
            <div className="border-t border-ink-800 pt-4">
              <ReaderPicker
                projectId={projectId}
                chosen={chosen}
                personal={personal}
                onReview={(captureId, learned) => {
                  onClose();
                  onReview(captureId, learned);
                }}
              />
            </div>
          ) : (
            <Button variant="secondary" icon={<Plus size={16} />} onClick={() => setAdding(true)}>
              Add a reader
            </Button>
          ))}
      </Modal>

      {/* Edits change this project's copy only; another project using the same
          reader keeps its own. */}
      <ReaderEditor
        open={editing !== null}
        reader={editing}
        onClose={() => setEditing(null)}
        onSubmit={async (draft) => {
          if (!editing) return;
          const updated: ProjectReader = { ...editing, ...cleanDraft(draft) };
          await setProjectReaders(
            projectId,
            chosen.map((r) => (r.id === editing.id ? updated : r)),
          );
        }}
      />

      <Modal open={!!removing} onClose={() => setRemoving(null)} title="Remove reader">
        <p className="text-sm text-ink-300">
          Stop recording <strong className="text-ink-100">{removing?.name}</strong> in this
          project? Readings already saved with it stay in the project and in the export.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (removing) {
                await setProjectReaders(
                  projectId,
                  chosen.filter((r) => r.id !== removing.id),
                );
              }
              setRemoving(null);
            }}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </>
  );
}
